// RateControl — labeled percentage lever: a − / editable number / + stepper
// over a range slider, with an optional hint and invalid state. Controlled
// (parent owns value). Keyboard- and screen-reader-accessible. From the
// prototype's RateControl (m2-rate-ctrl + m2-stepper).
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import './rate-control.css';

const ACCENTS = {
  blue: 'var(--brand-blue)',
  green: 'var(--brand-green)',
  purple: 'var(--brand-purple)',
  orange: 'var(--brand-amber)',
};

const MinusGlyph = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const PlusGlyph = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function RateControl({
  label,
  value,
  onChange,
  min = 0,
  max = 50,
  step = 1,
  suffix = '%',
  accent = 'blue',
  hint,
  invalid = false,
  sub,
  className,
}) {
  const color = ACCENTS[accent] || ACCENTS.blue;
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  const fieldId = `rate-${String(label).toLowerCase().replace(/[^a-z0-9]/g, '')}`;
  const clamp = (v) => Math.max(min, Math.min(max, Number.isFinite(v) ? Math.round(v) : min));

  return (
    <div className={cx('rate-control', invalid && 'is-invalid', className)} style={{ '--rate-accent': color }}>
      <div className="rate-control__top">
        <label className="rate-control__label" htmlFor={fieldId}>
          {label}
        </label>
        <div className="rate-control__stepper">
          <button
            type="button"
            className="rate-control__step"
            aria-label={`Decrease ${label}`}
            disabled={value <= min}
            onClick={() => onChange(clamp(value - step))}
          >
            <MinusGlyph />
          </button>
          <span className="rate-control__field">
            <input
              id={fieldId}
              type="number"
              inputMode="numeric"
              min={min}
              max={max}
              step={step}
              value={value}
              aria-label={typeof label === 'string' ? `${label} ${suffix === '%' ? 'percent' : ''}`.trim() : 'Rate'}
              aria-invalid={invalid}
              onChange={(e) => onChange(clamp(parseFloat(e.target.value)))}
            />
            <span className="rate-control__suffix">{suffix}</span>
          </span>
          <button
            type="button"
            className="rate-control__step"
            aria-label={`Increase ${label}`}
            disabled={value >= max}
            onClick={() => onChange(clamp(value + step))}
          >
            <PlusGlyph />
          </button>
        </div>
      </div>
      <input
        type="range"
        className="rate-control__slider"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={typeof label === 'string' ? label : 'Rate'}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ '--rate-fill': `${pct}%` }}
      />
      {sub && <span className="rate-control__sub">{sub}</span>}
      {hint && <span className="rate-control__hint">{hint}</span>}
    </div>
  );
}

RateControl.propTypes = {
  label: PropTypes.node,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  suffix: PropTypes.string,
  accent: PropTypes.oneOf(['blue', 'green', 'purple', 'orange']),
  hint: PropTypes.node,
  invalid: PropTypes.bool,
  sub: PropTypes.node,
  className: PropTypes.string,
};

export default RateControl;
